// src/core/redux/services/adminTenantApi.ts
//
// Tenant 360 — the platform-admin view of a single tenant, backed by the backend's
// /admin/tenants/:id/* sub-resources plus lifecycle mutations. Injected into baseApi
// so every call is cached, tagged, and auth-refreshed.

import { baseApi } from './baseApi';

// Backend responses are sometimes { data: ... } and sometimes the raw payload.
const unwrap = (r: any) => (r && typeof r === 'object' && 'data' in r ? r.data : r);

export interface AdminTenant {
  id: string;
  name?: string;
  slug?: string;
  type?: string;
  status?: string;
  isActive?: boolean;
  /** Set once archived (soft-deleted). Null/absent for a live tenant. */
  deletedAt?: string | null;
  deletionReason?: string | null;
  contactEmail?: string;
  createdAt?: string;
  [k: string]: any;
}

/**
 * What archiving a tenant costs and what it keeps.
 *
 * `retained` is the point: the platform does not hard-delete tenants, so the
 * dialog shows these as kept rather than as a body count.
 */
export interface TenantArchiveImpact {
  tenant: { id: string; name?: string; isSystem?: boolean; isActive?: boolean; deletedAt?: string | null };
  revocable: { activeSessions: number };
  retained: {
    users: number;
    stores: number;
    warehouses: number;
    customers: number;
    transactions: number;
    invoices: number;
    payments: number;
    journalEntries: number;
    purchaseOrders: number;
    patients: number;
    prescriptions: number;
  };
  counterparty: { b2bPartners: number };
}

export interface ArchiveResult {
  id?: string;
  deletedAt?: string;
  revokedSessions?: number;
  message?: string;
}

export const adminTenantApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTenants: builder.query<AdminTenant[], void>({
      query: () => 'admin/tenants',
      transformResponse: (r: any) => {
        const v = r && typeof r === 'object' ? (r.data ?? r.tenants ?? r) : r;
        return Array.isArray(v) ? v : [];
      },
      providesTags: [{ type: 'Tenant', id: 'LIST' }],
    }),
    getTenant: builder.query<AdminTenant, string>({
      query: (id) => `admin/tenants/${id}`,
      transformResponse: unwrap,
      providesTags: (_r, _e, id) => [{ type: 'Tenant', id }],
    }),

    getTenantStores: builder.query<any[], string>({
      query: (id) => `admin/tenants/${id}/stores`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'TenantStores', id }],
    }),
    getTenantWarehouses: builder.query<any[], string>({
      query: (id) => `admin/tenants/${id}/warehouses`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'TenantWarehouses', id }],
    }),
    getTenantUsers: builder.query<any[], string>({
      query: (id) => `admin/tenants/${id}/users`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'TenantUsers', id }],
    }),
    getTenantCustomers: builder.query<any[], string>({
      query: (id) => `admin/tenants/${id}/customers`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'TenantCustomers', id }],
    }),
    getTenantTransactions: builder.query<any[], string>({
      query: (id) => `admin/tenants/${id}/transactions`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'TenantTransactions', id }],
    }),
    getTenantPayments: builder.query<any[], string>({
      query: (id) => `admin/tenants/${id}/payments`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'TenantPayments', id }],
    }),
    getTenantInventories: builder.query<any[], string>({
      query: (id) => `admin/tenants/${id}/inventories`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'TenantInventories', id }],
    }),
    getTenantPosSessions: builder.query<any[], string>({
      query: (id) => `admin/tenants/${id}/pos-sessions`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'TenantPosSessions', id }],
    }),
    /**
     * Health + the domains support actually gets called about.
     *
     * These sit on the resource's own `/{resource}/tenant/:tenantId` route
     * rather than under `/admin/tenants/:id/*` — the backend already exposed
     * them there, so extending Tenant 360 needed no new server endpoints.
     */
    getTenantHealth: builder.query<any, string>({
      query: (id) => `admin/tenants/${id}/health`,
      // Not unwrapped: the report IS the payload (tenant/overall/checks/activity).
      providesTags: (_r, _e, id) => [{ type: 'Tenant', id }],
    }),
    getTenantInvoices: builder.query<any[], string>({
      query: (id) => `admin/invoices/tenant/${id}`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'Tenant', id }],
    }),
    getTenantPurchaseOrders: builder.query<any[], string>({
      query: (id) => `admin/purchase-order/tenant/${id}`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'Tenant', id }],
    }),
    getTenantQuotations: builder.query<any[], string>({
      query: (id) => `admin/quotations/tenant/${id}`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'Tenant', id }],
    }),
    getTenantShipments: builder.query<any[], string>({
      query: (id) => `admin/shipments/tenant/${id}`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'Tenant', id }],
    }),
    getTenantStockTransfers: builder.query<any[], string>({
      query: (id) => `admin/stock-transfers/tenant/${id}`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'Tenant', id }],
    }),
    getTenantB2bConnections: builder.query<any[], string>({
      query: (id) => `admin/b2bconnections/${id}`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'Tenant', id }],
    }),
    getTenantSettings: builder.query<any[], string>({
      query: (id) => `admin/settings/tenant/${id}`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'Tenant', id }],
    }),
    getTenantKyc: builder.query<any, string>({
      query: (id) => `admin/kyc/${id}`,
      transformResponse: (r: any) => unwrap(r),
      providesTags: (_r, _e, id) => [{ type: 'Kyc', id }],
    }),
    /** Per-tenant timelines — what happened, and what went wrong. */
    getTenantActivity: builder.query<any[], string>({
      query: (id) => `admin/audits/${id}?limit=100`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'AuditLog', id }],
    }),
    getTenantErrors: builder.query<any[], string>({
      query: (id) => `admin/system-logs?tenantId=${id}&limit=100`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'AuditLog', id }],
    }),

    getTenantBusinessEntities: builder.query<any[], string>({
      query: (id) => `admin/tenants/${id}/business-entities`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'TenantBusinessEntities', id }],
    }),
    getTenantSuppliers: builder.query<any[], string>({
      query: (id) => `admin/tenants/${id}/suppliers`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'TenantSuppliers', id }],
    }),
    getTenantProductSuppliers: builder.query<any[], string>({
      query: (id) => `admin/tenants/${id}/product-suppliers`,
      transformResponse: (r: any) => unwrap(r) ?? [],
      providesTags: (_r, _e, id) => [{ type: 'TenantProductSuppliers', id }],
    }),

    // --- Lifecycle mutations ---
    updateTenant: builder.mutation<AdminTenant, { id: string } & Partial<AdminTenant>>({
      query: ({ id, ...body }) => ({ url: `admin/tenants/${id}`, method: 'PUT', body }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Tenant', id }],
    }),
    deactivateTenant: builder.mutation<AdminTenant, string>({
      query: (id) => ({ url: `admin/tenants/${id}/deactivate`, method: 'PATCH' }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, id) => [{ type: 'Tenant', id }],
    }),
    reactivateTenant: builder.mutation<AdminTenant, string>({
      query: (id) => ({ url: `admin/tenants/${id}/reactivate`, method: 'PATCH' }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, id) => [{ type: 'Tenant', id }],
    }),
    /**
     * Counts behind the archive confirmation. Fetched only when the dialog opens
     * so the console can say what is actually at stake for THIS tenant.
     */
    getTenantArchiveImpact: builder.query<TenantArchiveImpact, string>({
      query: (id) => `admin/tenants/${id}/archive-impact`,
      transformResponse: unwrap,
    }),

    /**
     * Archives the tenant. The backend performs a SOFT delete — no rows are
     * removed — so this is reversible via restoreTenant. The endpoint keeps the
     * DELETE verb it has always had; only the semantics are honest now.
     */
    archiveTenant: builder.mutation<ArchiveResult, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `admin/tenants/delete/${id}`,
        method: 'DELETE',
        body: { reason },
      }),
      transformResponse: (r: any) => ({ ...unwrap(r), message: r?.message }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Tenant', id }, { type: 'Tenant', id: 'LIST' }],
    }),
    restoreTenant: builder.mutation<AdminTenant, string>({
      query: (id) => ({ url: `admin/tenants/${id}/restore`, method: 'PATCH' }),
      transformResponse: unwrap,
      invalidatesTags: (_r, _e, id) => [{ type: 'Tenant', id }, { type: 'Tenant', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetTenantHealthQuery,
  useGetTenantInvoicesQuery,
  useGetTenantPurchaseOrdersQuery,
  useGetTenantQuotationsQuery,
  useGetTenantShipmentsQuery,
  useGetTenantStockTransfersQuery,
  useGetTenantB2bConnectionsQuery,
  useGetTenantSettingsQuery,
  useGetTenantKycQuery,
  useGetTenantActivityQuery,
  useGetTenantErrorsQuery,
  useGetTenantsQuery,
  useGetTenantQuery,
  useGetTenantStoresQuery,
  useGetTenantWarehousesQuery,
  useGetTenantUsersQuery,
  useGetTenantCustomersQuery,
  useGetTenantTransactionsQuery,
  useGetTenantPaymentsQuery,
  useGetTenantInventoriesQuery,
  useGetTenantPosSessionsQuery,
  useGetTenantBusinessEntitiesQuery,
  useGetTenantSuppliersQuery,
  useGetTenantProductSuppliersQuery,
  useUpdateTenantMutation,
  useDeactivateTenantMutation,
  useReactivateTenantMutation,
  useLazyGetTenantArchiveImpactQuery,
  useArchiveTenantMutation,
  useRestoreTenantMutation,
} = adminTenantApi;
